import dayjs from 'dayjs';
import { DATE_FORMAT_API, SMALL_MEDIA_QUERY } from '../../../common/components/utils/constants';
import { CalendarCustom } from '../../../common/components/CalendarCustom';
import { dateGetMonthRange } from '../../../common/components/utils/dateGetMonthRange';

const SELECTORS = {
	dateInput: '[data-rangepicker-treatment]', // Intentionally scope to `clinic__main` because of the header
	bookingForm: '[data-type="booking-form"]',
	dialysisOptions: '[data-type="booking-dialysis-option"]',
	notAvailableInfoBox: '[data-type="not-available-days--info-box"]',
	notAvailableCount: '[data-type="not-available-days--count"]',
	pricePlaceholder: '[data-price-placeholder]',
};

export class TreatmentDays {
	constructor({ dateInput, bookingForm }) {
		this.bookingForm = bookingForm;
		this.dateInput = dateInput;
		this.dialysisOptions = [...document.querySelectorAll(SELECTORS.dialysisOptions)];
		this.notAvailableInfoBox = document.querySelector(SELECTORS.notAvailableInfoBox);
		this.notAvailableCount = document.querySelector(SELECTORS.notAvailableCount);
		this.pricePlaceholder = document.querySelector(SELECTORS.pricePlaceholder);

		const requiredElements = [
			this.dateInput,
			this.dialysisOptions,
			this.notAvailableInfoBox,
			this.notAvailableCount,
		];
		if (requiredElements.some((ele) => !ele)) {
			throw new Error('TreatmentDays not initialized');
		}

		this.mediaQuery = window.matchMedia(SMALL_MEDIA_QUERY);
		this.treatmentCalendars = [];
		this.skipOnSelected = false;
		this.allDisabledDates = [];

		this.bindEvents();
		this.setup();
		this.setupCalendar({
			...this.getSizeOptions(),
		});
	}

	static init() {
		const dateInputs = [...document.querySelectorAll(SELECTORS.dateInput)];
		const bookingForm = document.querySelector(SELECTORS.bookingForm);

		/**
		 * This dateInputs will have same behaviour but
		 * there options will be different if they are different in templates(html)
		 */
		let isParentForm = false;
		const treatmentCalendars = dateInputs.map((dateInput) => {
			isParentForm = bookingForm.contains(dateInput);
			return new TreatmentDays({
				dateInput,
				bookingForm: isParentForm ? bookingForm : null,
			});
		});
		treatmentCalendars.forEach((treatmentDay) =>
			treatmentDay.setTreatmentCalendars(treatmentCalendars)
		);
	}

	/**
	 * Bind `this` explicitly for event listeners.
	 */
	bindEvents() {
		['onChangeDialysisOptions', 'onMediaQueryChange'].forEach((e) => {
			this[e] = this[e].bind(this);
		});
	}

	/**
	 * Is mobile media query met.
	 * @return {boolean}
	 */
	isMobile() {
		return this.mediaQuery.matches;
	}

	getSizeOptions() {
		return {
			numberOfColumns: this.isMobile() ? 1 : 2,
			numberOfMonths: this.isMobile() ? 1 : 2,
		};
	}

	async setupCalendar(options) {
		this.calendar = new CalendarCustom(this.dateInput, {
			setup: (instance) => {
				instance.on('selected', (...dates) => {
					CalendarCustom.emitEventSelected({ instance, dates });

					if (this.skipOnSelected) {
						this.skipOnSelected = false;
						return;
					}

					this.syncTreatmentCalendars({ dates });
					this.handleSelectedDisabledDates({ dates });
				});

				instance.on('before:show', async () => {
					const {
						options: { startDate, endDate },
					} = instance;
					const dates = [startDate, endDate];

					await this.updateDisabledDates({ instance });
					this.handleSelectedDisabledDates({ dates });
				});

				instance.on('change:month', async () => {
					await this.updateDisabledDates({ instance });
				});
			},
			...options,
		});

		await this.updateDisabledDates({ instance: this.calendar.instance });
		const dates = [this.calendar.instance.getStartDate(), this.calendar.instance.getEndDate()];
		this.handleSelectedDisabledDates({ dates });
	}

	syncTreatmentCalendars({ dates }) {
		/**
		 * Update other calendar that needs to be in sync with this one.
		 */
		this.treatmentCalendars
			.filter((tCalendar) => tCalendar.getRangeId() !== this.getRangeId())
			.forEach((tc) => {
				// eslint-disable-next-line no-param-reassign
				tc.skipOnSelected = true;
				tc.calendar.instance.setDateRange(...dates);
			});
	}

	setTreatmentCalendars(calendars) {
		this.treatmentCalendars = calendars;
	}

	handleSelectedDisabledDates({ dates }) {
		if (dates.some((date) => !date)) {
			return;
		}

		let [from, to] = dates;
		[from, to] = [
			dayjs(from.dateInstance).format(DATE_FORMAT_API),
			dayjs(to.dateInstance).format(DATE_FORMAT_API),
		];
		[from, to] = [dayjs(from), dayjs(to)];

		const selectedDisableDates = this.allDisabledDates.filter(
			(date) => from.isSameOrBefore(date) && to.isSameOrAfter(date)
		);

		const { callback } = this.notAvailableCount.dataset;
		this.notAvailableCount.textContent = window[callback]?.(selectedDisableDates.length);
		this.notAvailableInfoBox.style.display = selectedDisableDates.length ? 'block' : 'none';
	}

	getRangeId() {
		return this.dateInput.dataset.rangepickerId;
	}

	getSelectedDialysisType() {
		const radioChecked = this.dialysisOptions.find((radio) => radio.checked);
		return radioChecked.value;
	}

	async updateDisabledDates({ instance }) {
		const [date1] = instance.calendars;
		const date2 = dayjs(date1.dateInstance).add(1, 'month');

		const { from } = dateGetMonthRange({
			date: date1.dateInstance,
			format: DATE_FORMAT_API,
		});

		const { to } = dateGetMonthRange({
			date: date2,
			format: DATE_FORMAT_API,
		});

		const {
			data: { data },
		} = await http.get(clinicAvailabilityURL, {
			params: {
				from,
				to,
			},
		});

		const selectedDialysis = this.getSelectedDialysisType();
		const disableDates = data.slots
			.filter((status) => !status.overall?.[selectedDialysis])
			.map((status) => status.date);

		instance.setLockDays(disableDates);

		this.allDisabledDates = [...new Set([...this.allDisabledDates, ...disableDates])];
	}

	/**
	 * When the media query changes reinitialize calendar.
	 * @param {MediaQueryListEvent} event
	 */
	onMediaQueryChange() {
		this.calendar.instance.setOptions({
			...this.getSizeOptions(),
		});
	}

	onChangeDialysisOptions(event) {
		const { price } = event.target.dataset;
		if (price && this.pricePlaceholder) {
			this.pricePlaceholder.textContent = price;
		}

		this.treatmentCalendars.forEach((tCalendar) => {
			this.updateDisabledDates({ instance: tCalendar.calendar.instance });
		});

		try {
			const trackingParams = JSON.parse(event.target.dataset.trackingParams);
			TreatmentDays.trackDialysisOptionChange(trackingParams);
		} catch (err) {
			console.error(err);
		}
	}

	static trackDialysisOptionChange(trackingParams) {
		if (!trackingParams) return;

		window.dataLayer.push({ ecommerce: null });
		window.dataLayer.push({
			event: 'view_item',
			ecommerce: {
				currency: trackingParams.currency,
				value: trackingParams.price,
				items: [
					{
						item_id: trackingParams.item_id,
						item_name: trackingParams.item_name,
						item_brand: trackingParams.brand,
						item_category: trackingParams.category,
						item_category2: trackingParams.category2,
						item_variant: trackingParams.variant,
						item_list_name: trackingParams.list_name,
						item_list_id: trackingParams.list_id,
						price: trackingParams.price,
						currency: trackingParams.currency,
						quantity: 1,
					},
				],
			},
		});
	}

	setupEventListeners() {
		this.bookingForm?.addEventListener('change', this.onChangeDialysisOptions);
	}

	setup() {
		this.mediaQuery.addEventListener('change', this.onMediaQueryChange);
		this.setupEventListeners();
	}
}
